/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { withRouter, Link } from 'react-router';
import { ExperimentName } from '@letsdothis/experiments';
import useExperiment from '@hooks/useExperiment';
import Auth from '@services/auth';
import { Button } from '@letsdothis/lds/lib/Button';

import EmailPasswordLogin from './EmailPasswordLogin';
import ProviderLogin from './ProviderLogin';
import { STAGES } from './enums';
import {
  LoginComponentParams,
  LoginFullPageParams,
  LoginNoBackgroundParams,
  LoginOptionsParams,
  LoginContentParams,
} from './Types';
import * as styles from './styles.module.css';
import ErrorMessage from '../Form/Fields/ErrorMessage';
import { ErrorMessageParams } from '../Form/Fields/Types';

const OldOptionsContainer = ({ returnUrl }: LoginOptionsParams) => {
  return (
    <div className={styles.desktopLoginOptionsContainer}>
      {ProviderLogin({ returnUrl })}
      <div className={styles.terms}>
        By creating an account, you accept our{' '}
        <Link target="_blank" rel="noopener noreferrer nofollow" to="/terms">
          terms
        </Link>
      </div>
    </div>
  );
};

const NewOptionsContainer = ({
  returnUrl,
  setStage,
  client,
  location,
  router,
  showProviderLogin,
  setShowProviderLogin,
}: LoginOptionsParams) => {
  return (
    <div className={styles.desktopLoginOptionsContainer}>
      {showProviderLogin && (
        <>
          {ProviderLogin({ returnUrl })}
          <div className={styles.or}>OR</div>
        </>
      )}
      {EmailPasswordLogin({
        returnUrl,
        setStage,
        client,
        location,
        router,
        showProviderLogin,
        setShowProviderLogin,
      })}
    </div>
  );
};

const LoginOptions = ({
  returnUrl,
  setStage,
  client,
  location,
  router,
}: LoginOptionsParams) => {
  const [showProviderLogin, setShowProviderLogin] = useState(true);
  const emailPasswordExperiment = useExperiment(
    ExperimentName.EMAIL_PASSWORD_LOGIN,
    {
      persist: true,
    },
  );
  const isLoginExperimentVariant = useExperiment(ExperimentName.LDT_LOGIN);
  Auth.redirectToLegacyAuthIfStrava(isLoginExperimentVariant);
  const containerProps = {
    returnUrl,
    setStage,
    client,
    location,
    router,
    showProviderLogin,
    setShowProviderLogin,
  };
  return (
    <>
      {showProviderLogin && (
        <>
          <h1 className={styles.desktopTitle}>Let&apos;s get you signed in</h1>
          <h2 className={styles.desktopBlurb}>Simply select an option below</h2>
        </>
      )}
      {!emailPasswordExperiment && <OldOptionsContainer {...containerProps} />}
      {emailPasswordExperiment && <NewOptionsContainer {...containerProps} />}
      {isLoginExperimentVariant && (
        <div className={styles.stravaClickback}>
          Previously logged in with strava?{' '}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={Auth.redirectToLegacyAuthWithStrava}>
            Click here
          </a>
        </div>
      )}
    </>
  );
};

const SignUpComplete = ({ onClick }: { onClick: () => void }) => {
  return (
    <>
      <h1 className={styles.desktopTitle}>Please verify your account.</h1>
      <h2 className={styles.desktopBlurb}>
        Please check your email to verify your account. Once verified, click the
        button below and sign in.
      </h2>
      <Button onClick={onClick}>Im verified. Sign In</Button>
    </>
  );
};

const ErrorDisplay = ({ error }: ErrorMessageParams) => {
  return (
    <>
      <h1 className={styles.desktopTitle}>Login has failed.</h1>
      <h2 className={styles.desktopBlurb}>
        {error && ErrorMessage({ error })}
      </h2>
    </>
  );
};

const LoginContent = (props: LoginContentParams) => {
  const [stage, setStage] = useState(STAGES.LOGIN);
  if (props.error) {
    return <ErrorDisplay error={props.error} />;
  }

  switch (stage) {
    case STAGES.LOGIN:
      return <LoginOptions {...props} setStage={setStage} />;
    case STAGES.SIGN_UP_COMPLETE:
      return SignUpComplete({ onClick: () => setStage(STAGES.LOGIN) });
    default:
      throw new Error(`Stage ${stage} does not exist.`);
  }
};

const LoginNoBackground = (props: LoginNoBackgroundParams) => {
  return (
    <div className={styles.desktopInnerContainer}>
      <LoginContent {...props} />
    </div>
  );
};

const LoginFullPage = (props: LoginFullPageParams) => {
  return (
    <div className={styles.desktopContainer}>
      <div className={styles.desktopInnerContainer}>
        <LoginContent {...props} />
      </div>
    </div>
  );
};

const LoginComponent = (props: LoginComponentParams): JSX.Element => {
  if (props.optionsOnly) return <LoginContent {...props} />;
  if (props.noBackground) return <LoginNoBackground {...props} />;
  return <LoginFullPage {...props} />;
};

export default withRouter(LoginComponent as any);
